import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Tab from '@material-ui/core/Tab'
import Tabs from '@material-ui/core/Tabs'
import { AgentMemoContent, AgentMemoDepartment, PermissionType } from '@golden/gdk-admin'
import { defaultEditorState, parseContent, EditorState } from '@golden/tiptap-react'
import { TextEditor } from '../default/form/textEditor/TextEditor'
import { useCommonStyles } from '../../utils/admin/StyleHook'
import { useChecker } from '../../utils/admin/AdminRouteHook'
import useT from '../../i18ns/admin/useT'

const useStyles = makeStyles((theme) => ({
  actions: {
    paddingBottom: theme.spacing(4)
  }
}))

const AGENT_MEMO_DEPARTMENTS: AgentMemoDepartment[] = ['rc', 'cs']

const agentMemoDepartmentLabel: Record<AgentMemoDepartment, string> = {
  rc: 'RC',
  cs: 'CS'
}

export const useReadableAgentMemoDepartments = (): AgentMemoDepartment[] => {
  const canReadRc = useChecker([PermissionType.PLAYER_MANAGEMENT_AGENTS_MEMO_RC_READ])
  const canReadCs = useChecker([PermissionType.PLAYER_MANAGEMENT_AGENTS_MEMO_CS_READ])
  return useMemo(() => {
    const readable: Record<AgentMemoDepartment, boolean> = {
      rc: canReadRc,
      cs: canReadCs
    }
    return AGENT_MEMO_DEPARTMENTS.filter((department) => readable[department])
  }, [canReadRc, canReadCs])
}

export const filterAgentMemoDepartments = (departments: AgentMemoDepartment[], emperorMemo: AgentMemoContent[], staffMemo: AgentMemoContent[]) => departments.filter((department) => (
  emperorMemo.some((item) => item.department === department) ||
  staffMemo.some((item) => item.department === department)
))

const AgentMemoContentBlock: React.FC<{ account: string, memo: string }> = ({ account, memo }) => {
  const [editorState, setEditorState] = useState<EditorState | null>(null)
  useEffect(() => {
    let alive = true
    void parseContent(memo).then((res) => {
      if (!alive) return
      setEditorState({
        ...defaultEditorState,
        content: res
      })
    })
    return () => { alive = false }
  }, [memo])
  if (editorState === null) return null
  return (
    <Box>
      <Box>
        <Typography variant="h4">
          {account}
        </Typography>
      </Box>
      <Box>
        <TextEditor
          value={editorState}
          disabled
          defaultHeight={false}
          controls={[]}
        />
      </Box>
    </Box>
  )
}

const AgentMemoButton: React.FC<{ emperorAccount: string, emperorMemo: AgentMemoContent[], staffAccount: string, staffMemo: AgentMemoContent[], departments: AgentMemoDepartment[] }> = ({ emperorAccount, emperorMemo, staffAccount, staffMemo, departments }) => {
  const commonClasses = useCommonStyles()
  const classes = useStyles()
  const { t } = useT()
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<AgentMemoDepartment | null>(null)
  const handleClose = useCallback(() => { setOpen(false) }, [])
  const handleTabChange = useCallback((_: React.ChangeEvent<unknown>, value: AgentMemoDepartment) => { setSelected(value) }, [])
  const tabs = useMemo(() => filterAgentMemoDepartments(departments, emperorMemo, staffMemo), [departments, emperorMemo, staffMemo])
  // 不用 state 同步 tabs，避免資料變動後殘留已不存在的選擇
  const department = (selected !== null && tabs.includes(selected)) ? selected : (tabs[0] ?? null)
  const currentEmperorMemo = emperorMemo.find((item) => item.department === department)
  const currentStaffMemo = staffMemo.find((item) => item.department === department)
  return (
    <>
      <Dialog open={open} fullWidth>
        <DialogContent>
          { departments.length > 1 && (
            <Tabs
              value={department}
              onChange={handleTabChange}
            >
              { tabs.map((item) => (
                <Tab
                  key={item}
                  value={item}
                  label={agentMemoDepartmentLabel[item]}
                />
              )) }
            </Tabs>
          ) }
          {
            currentEmperorMemo && (
              <Box marginTop={2}>
                <AgentMemoContentBlock key={`emperor-${department}`} account={emperorAccount} memo={currentEmperorMemo.memo} />
              </Box>
            )
          }
          {
            currentStaffMemo && (
              <Box marginTop={2}>
                <AgentMemoContentBlock key={`staff-${department}`} account={staffAccount} memo={currentStaffMemo.memo} />
              </Box>
            )
          }
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Grid container direction="row" spacing={2} justifyContent="center">
            <Grid item>
              <Button
                className={commonClasses.greyButton}
                onClick={handleClose}
              >
                {t('common.close')}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
      <span className={commonClasses.anchor} onClick={useCallback(() => { setOpen(true) }, [])}>{staffAccount}</span>
    </>
  )
}

export default AgentMemoButton
